import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { registerSchema, type RegisterInput } from "@platform/validation";
import { Button, Input, Label } from "@nthamizh/ui";
import { useRegister } from "@platform/hooks";
import { Link } from "react-router";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { MailCheck } from "lucide-react";

export function RegisterPage() {
  const registerMutation = useRegister();
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = handleSubmit(async (values) => {
    // No session is issued here — password-based signups must verify
    // their email before they can log in at all, so there's nothing to
    // auto-log-in with yet. See auth.service.ts's register for why.
    const result = (await registerMutation.mutateAsync(values)) as { requiresVerification: boolean; email: string };
    setSubmittedEmail(result.email);
  });

  if (submittedEmail) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
        <div className="w-full max-w-[380px] text-center">
          <div className="bg-white rounded-xl border border-gray-200 shadow-[0_1px_3px_0_rgb(0_0_0/0.06)] p-8">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center">
              <MailCheck className="h-6 w-6 text-blue-600" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">Check your email</h1>
            <p className="mt-2 text-sm text-gray-500">
              We sent a verification link to <span className="font-medium text-gray-700">{submittedEmail}</span>.
              Click it to activate your account, then sign in.
            </p>
            <Link
              to="/login"
              className="mt-6 inline-block text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-[380px]">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src="/logo.png" alt="Tailseed" className="h-16 w-16 object-contain" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">Create account</h1>
          <p className="mt-1 text-sm text-gray-500">Join your implementation team</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-[0_1px_3px_0_rgb(0_0_0/0.06)] p-6">
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                placeholder="Alex Johnson"
                {...register("name")}
                error={errors.name?.message}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                {...register("email")}
                error={errors.email?.message}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                {...register("password")}
                error={errors.password?.message}
              />
            </div>
            {registerMutation.isError && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
                Could not create account. Try again.
              </div>
            )}
            <Button type="submit" loading={registerMutation.isPending} className="w-full mt-1">
              Create account
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-xs text-gray-400">or</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          <GoogleSignInButton />

          <p className="mt-5 text-center text-sm text-gray-500">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-700">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
